import {countsGroupBySentimentFilterByAspectsQuery} from "../analyze/SentimentFetcher";
import ReviewReader from "../analyze/ReviewReader";
import React, {useState, useRef} from "react";
import {subYears} from 'date-fns';
import {useQuery} from "@apollo/client";
import {
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon, Box, Divider, Flex, Spacer, Spinner, Text, Tooltip
} from "@chakra-ui/react";
import SentimentBarGrid from "../analyze/SentimentBarGrid";
import SentimentSelector from "../analyze/SentimentSelectorButton";
import AspectSensitivity from "../analyze/AspectSensitivity";
import {InfoOutlineIcon} from "@chakra-ui/icons";


export default function SentimentSynchronizer({product_idsA, product_idsB, sharedAspects}) {
    const [renderReviewsA, setRenderReviewsA] = useState(false);
    const [renderReviewsB, setRenderReviewsB] = useState(false);
    const [reviewAspectA, setReviewAspectA] = useState("");
    const [reviewSentimentA, setReviewSentimentA] = useState("all");
    const [reviewAspectB, setReviewAspectB] = useState("");
    const [reviewSentimentB, setReviewSentimentB] = useState("all");
    const [expandAspects, setExpandAspects] = useState(false)
    const [expandSensitivity, setExpandSensitivity] = useState(false);
    const [startDate, setStartDate] = useState(subYears(new Date(), 10));
    const [endDate, setEndDate] = useState(new Date());
    const readerRefA = useRef();
    const readerRefB = useRef();


    console.log("Starting to fetch sentiment counts")
    console.log("product_idsA ", product_idsA, "product_idsB", product_idsB);
    let {loading: loadingA, error: errorA, data: dataA} = useQuery(countsGroupBySentimentFilterByAspectsQuery,
        {
            variables: {
                "product_ids": product_idsA,
                "aspects": sharedAspects,
                "start_date": startDate,
                "end_date": endDate
            },
        },
    );
    let {loading: loadingB, error: errorB, data: dataB} = useQuery(countsGroupBySentimentFilterByAspectsQuery,
        {
            variables: {
                "product_ids": product_idsB,
                "aspects": sharedAspects,
                "start_date": startDate,
                "end_date": endDate
            },
        },
    );
    if (loadingA || loadingB) {
        return <Box pt={{base: "130px", md: "80px", xl: "80px"}}><Spinner/> <Text>Loading...</Text></Box>
    }
    if (errorA || !dataA) {
        return (<div>Error, Please refresh to try again {errorA}</div>);
    }
    if (errorB || !dataB) {
        return (<div>Error, Please refresh to try again {errorB}</div>);
    }
    console.log("dataA", dataA);
    console.log("dataB", dataB);
    let aspectsASet = new Set()
    let aspectsBSet = new Set();
    let chartDataA = {}
    let chartDataB = {}
    for (let obj of dataA.counts_group_by_sentiment) {
        let asp = obj["aspect"];
        aspectsASet.add(asp);
        if (!chartDataA.hasOwnProperty(asp)) {
            chartDataA[asp] = {"positive": 0, "negative": 0, "neutral": 0};
        }
        chartDataA[asp][obj["sentiment"]] += obj["count"];
    }
    for (let obj of dataB.counts_group_by_sentiment) {
        let asp = obj["aspect"];
        aspectsBSet.add(asp);
        if (!chartDataB.hasOwnProperty(asp)) {
            chartDataB[asp] = {"positive": 0, "negative": 0, "neutral": 0};
        }
        chartDataB[asp][obj["sentiment"]] += obj["count"];
    }
    const commonAspects = Array.from(aspectsBSet).filter(x => aspectsASet.has(x))
    let sortable = [];
    for (let aspect of commonAspects) {
        sortable.push({"aspect": aspect, ...chartDataA[aspect]});
    }
    sortable.sort(function (a, b) {
        return (b["positive"] + b["neutral"] + b["negative"]) - (a["positive"] + a["negative"] + a["neutral"]);
    })
    let chartSeriesA = []
    let chartSeriesB = []
    let sorted_chart_categories = []

    for (let val of sortable) {

        chartSeriesA.push([{name: "Positive", data: [val["positive"]]}, {
            name: "Neutral",
            data: [val["neutral"]]
        }, {name: "Negative", data: [val["negative"]]}])
        chartSeriesB.push([{name: "Positive", data: [chartDataB[val["aspect"]].positive ?? 0]}, {
            name: "Neutral",
            data: [chartDataB[val["aspect"]].neutral ?? 0]
        }, {name: "Negative", data: [chartDataB[val["aspect"]].negative ?? 0]}])
        sorted_chart_categories.push(val["aspect"]);
    }

    const reviewTextA = reviewAspectA ? `Reviews - ${reviewAspectA}` : "Reviews"
    const reviewTextB = reviewAspectA ? `Reviews - ${reviewAspectB}` : "Reviews"

    function updateSelectedSentiment(sentiment) {
        setReviewSentimentA(sentiment);
        setReviewSentimentB(sentiment);
    }

    function onExpandAspects() {
        setExpandAspects(prevState => !prevState)
    }

    function handleReviewUpdate(aspect, sentiment) {
        console.log("handleReviewUpdate called");
        setRenderReviewsA(true);
        setRenderReviewsB(true);
        setReviewAspectA(aspect);
        setReviewAspectB(aspect);
        setReviewSentimentA(sentiment);
        setReviewSentimentB(sentiment);
    }

    function handleScrollA(scroll) {
        readerRefB.current.scrollTop = scroll.target.scrollTop;
    }

    function handleScrollB(scroll) {
        readerRefA.current.scrollTop = scroll.target.scrollTop;
    }


    console.log("commonAspects", commonAspects.length);
    console.log("sorted_chart_categories", sorted_chart_categories);
    console.log("expandSensitivity", expandSensitivity);
    return (
        <>
            <Box mr='30px'>
                <Text fontSize='xl' mt='30px' fontWeight="bold">Aspects</Text>
                <Divider/>
                <SentimentBarGrid series={chartSeriesA} categories={sorted_chart_categories}
                                  handleClick={handleReviewUpdate} expandAspects={expandAspects}
                                  onExpandAspects={onExpandAspects} selectedAspect={reviewAspectA}
                                  readerRef={readerRefA} handleScroll={handleScrollA}/>
                <Accordion index={expandSensitivity ? [0] : []} allowToggle
                           onChange={() => setExpandSensitivity(prevState => !prevState)}>
                    <AccordionItem>
                        <AccordionButton>
                            <Box flex='1' textAlign='left'>
                                <Text fontSize='xl' fontWeight="bold">
                                    Sensitivity of Ratings for Aspects
                                    <Tooltip label="High sensitivity = better sentiment for the aspect means better product ratings and low sentiment for the aspect means poor product ratings.
                 Low sensitivity = better or worse sentiment for the aspect does not move the product rating too much."
                                    ><InfoOutlineIcon ml="10px"/></Tooltip>
                                </Text>
                            </Box>
                            <AccordionIcon/>
                        </AccordionButton>

                        <AccordionPanel pb={4}>
                            <AspectSensitivity product_ids={product_idsA} show_title={false}/>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>

                <Flex direction='row' mt='30px'>
                    <Text fontSize='xl' fontWeight="bold">{reviewTextA}</Text>
                    <Spacer/>
                    <SentimentSelector reviewSentiment={reviewSentimentA}
                                       setReviewSentiment={updateSelectedSentiment}/>
                </Flex>
                <Divider mt="2px"/>
                <ReviewReader renderReviews={renderReviewsA} product_ids={product_idsA}
                              aspect={reviewAspectA}
                              sentiment={reviewSentimentA} setReviewSentiment={updateSelectedSentiment}/>
            </Box>
            <Box>
                <Text fontSize='xl' mt='30px' fontWeight="bold">Aspects</Text>
                <Divider/>
                <SentimentBarGrid series={chartSeriesB} categories={sorted_chart_categories}
                                  handleClick={handleReviewUpdate} expandAspects={expandAspects}
                                  onExpandAspects={onExpandAspects} selectedAspect={reviewAspectB}
                                  readerRef={readerRefB} handleScroll={handleScrollB}/>
                <Accordion index={expandSensitivity ? [0] : []} allowToggle
                           onChange={() => setExpandSensitivity(prevState => !prevState)}>
                    <AccordionItem>
                        <AccordionButton>
                            <Box flex='1' textAlign='left'>
                                <Text fontSize='xl' fontWeight="bold">
                                    Sensitivity of Ratings for Aspects
                                    <Tooltip label="High sensitivity = better sentiment for the aspect means better product ratings and low sentiment for the aspect means poor product ratings.
                 Low sensitivity = better or worse sentiment for the aspect does not move the product rating too much."
                                    ><InfoOutlineIcon ml="10px"/></Tooltip>
                                </Text>
                            </Box>
                            <AccordionIcon/>
                        </AccordionButton>

                        <AccordionPanel pb={4}>
                            <AspectSensitivity product_ids={product_idsB} show_title={false}/>
                        </AccordionPanel>
                    </AccordionItem>
                </Accordion>
                <Flex direction='row' mt='30px'>
                    <Text fontSize='xl' fontWeight="bold">{reviewTextB}</Text>
                    <Spacer/>
                    <SentimentSelector reviewSentiment={reviewSentimentB}
                                       setReviewSentiment={updateSelectedSentiment}/>
                </Flex>
                <Divider mt="2px"/>
                <ReviewReader renderReviews={renderReviewsB} product_ids={product_idsB}
                              aspect={reviewAspectB}
                              sentiment={reviewSentimentB} setReviewSentiment={updateSelectedSentiment}/>
            </Box>

        </>
    )
}